import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useStorage } from '../hooks/useStorage'

// Mock data para áreas
const areaNames = {
  'compliance': 'Compliance',
  'engenharia-dados': 'Eng. de Dados',
  'comercial': 'Comercial'
}

export default function CreateProcessPage() {
  const { areaId } = useParams<{ areaId: string }>()
  const navigate = useNavigate()
  const [processName, setProcessName] = useState('')
  const [description, setDescription] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    uploadFile,
    isLoading: uploadLoading,
    error: uploadError,
    uploadProgress,
    clearError
  } = useStorage({
    onSuccess: () => {
      console.log('Upload concluído com sucesso!')
    },
    onError: (error) => {
      console.error('Erro no upload:', error)
    }
  })

  const areaName = areaId ? areaNames[areaId as keyof typeof areaNames] || 'Área' : 'Área'

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setSelectedFile(file)
      clearError()
    }
  }

  const removeFile = () => {
    setSelectedFile(null)
    clearError()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!processName.trim()) {
      alert('Nome do processo é obrigatório')
      return
    }

    setIsSubmitting(true)

    try {
      let videoUrl = null

      // Se há um arquivo selecionado, faz o upload
      if (selectedFile) {
        const uploadResult = await uploadFile('videos', selectedFile)
        if (uploadResult) {
          videoUrl = uploadResult.url
        } else {
          throw new Error('Falha no upload do vídeo')
        }
      }

      // Aqui você salvaria no banco de dados
      const processData = {
        name: processName,
        description,
        areaId,
        videoUrl,
        createdAt: new Date().toISOString(),
        status: 'Em andamento'
      }

      console.log('Dados do processo:', processData)

      // Simular salvamento no banco
      await new Promise(resolve => setTimeout(resolve, 1000))

      // Redirecionar para a área
      navigate(`/areas/${areaId}`)
    } catch (error) {
      console.error('Erro ao criar processo:', error)
      alert('Erro ao criar processo. Tente novamente.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const isFormValid = processName.trim().length > 0

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="mb-6">
          <Link to="/" className="text-gray-500 hover:text-gray-700">Início</Link>
          <span className="mx-2 text-gray-400">/</span>
          <Link to={`/areas/${areaId}`} className="text-gray-500 hover:text-gray-700">{areaName}</Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-900 font-medium">Criar Processo</span>
        </nav>

        {/* Header */}
        <div className="bg-white rounded-xl p-6 shadow-sm mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Criar Novo Processo</h1>
          <p className="text-gray-600">Área: {areaName}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="max-w-3xl">
          <div className="bg-white rounded-xl p-6 shadow-sm space-y-6">
            {/* Nome do Processo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome do Processo *
              </label>
              <input
                type="text"
                value={processName}
                onChange={(e) => setProcessName(e.target.value)}
                placeholder="Ex: Auditoria Interna Q1"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            {/* Descrição */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descrição
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Descreva o objetivo e escopo do processo..."
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Upload de Vídeo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vídeo do Processo (opcional)
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-gray-400 transition-colors">
                {!selectedFile ? (
                  <div className="text-center">
                    <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                      <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <div className="flex text-sm text-gray-600">
                      <label className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                        <span>Clique para selecionar</span>
                        <input
                          type="file"
                          className="sr-only"
                          accept="video/*"
                          onChange={handleFileSelect}
                        />
                      </label>
                      <p className="pl-1">ou arraste um arquivo aqui</p>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">MP4, MOV, AVI até 50MB</p>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <svg className="h-8 w-8 text-blue-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                        <p className="text-xs text-gray-500">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={removeFile}
                      className="text-red-500 hover:text-red-700"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>

              {/* Progress Bar */}
              {uploadLoading && uploadProgress > 0 && (
                <div className="mt-4">
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div 
                      className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" 
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">Upload: {uploadProgress}%</p>
                </div>
              )}

              {/* Upload Error */}
              {uploadError && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-red-800 text-sm">{uploadError.message}</p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
              <Link
                to={`/areas/${areaId}`}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </Link>
              <button
                type="submit"
                disabled={!isFormValid || isSubmitting || uploadLoading}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Criando...' : 'Criar Processo'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
